import { getBrowserRuntimeEnv, isBrowserDevMode } from "./browser-runtime-urls";

type DiagnosticLevel = "debug" | "info" | "warn" | "error";
type DiagnosticErrorDetails = {
  readonly message: unknown;
  readonly cause: unknown;
  readonly error: unknown;
  readonly _tag: unknown;
  readonly stack: unknown;
};

const SENSITIVE_KEY_PATTERN =
  /(authorization|bearer|cookie|credential|dob|egk|email|family|given|kvnr|name|password|patient|phone|pin|secret|stack|token)/i;
const SENSITIVE_MESSAGE_PATTERN =
  /\b(authorization|bearer|cookie|credential|dob|egk|email|family|given|kvnr|name|password|patient|phone|pin|secret|stack|token|id|document|endpoint|task)\b\s*[:=]?\s*[^,\s)]+/gi;

function isClientDiagnosticsEnabled(): boolean {
  const env = getBrowserRuntimeEnv();

  if (env?.VITE_ASCLEO_CLIENT_DIAGNOSTICS === "true") {
    return true;
  }

  if (typeof localStorage === "undefined") {
    return false;
  }

  return (
    isBrowserDevMode(env) &&
    localStorage.getItem("ascleo.client.diagnostics") === "true"
  );
}

function sanitizeDiagnosticValue(value: unknown, depth = 0): unknown {
  if (value == null) return value;
  if (typeof value === "string") {
    return { stringLength: value.length, hasValue: value.length > 0 };
  }
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value instanceof Error) {
    return { errorType: value.name || "Error", hasMessage: value.message.length > 0 };
  }
  if (depth >= 2) return "[redacted-depth]";
  if (Array.isArray(value)) return { itemCount: value.length };
  if (typeof value !== "object") return typeof value;

  return Object.fromEntries(
    Object.keys(value).map((key) => [
      key,
      SENSITIVE_KEY_PATTERN.test(key)
        ? "[redacted]"
        : sanitizeDiagnosticValue(Reflect.get(value, key), depth + 1),
    ]),
  );
}

function sanitizeDiagnosticMessage(message: string): string {
  return message
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, "[id]")
    .replace(/https?:\/\/\S+/gi, "[url]")
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[email]")
    .replace(SENSITIVE_MESSAGE_PATTERN, (_match, label: string) => `${label} [redacted]`);
}

function emitDiagnostic(level: DiagnosticLevel, message: string, values: readonly unknown[]): void {
  if (!isClientDiagnosticsEnabled()) return;

  const safeValues = values.map((value) => sanitizeDiagnosticValue(value));
  globalThis.console[level](sanitizeDiagnosticMessage(message), ...safeValues);
}

/**
 * Extract stable diagnostic fields from an unknown error object.
 *
 * @since 2026-06-21
 * @example
 * ```ts
 * if (error && typeof error === "object") {
 *   diagnosticLogger.error("Error details:", unknownErrorDiagnosticDetails(error));
 * }
 * ```
 */
export function unknownErrorDiagnosticDetails(error: object): DiagnosticErrorDetails {
  return {
    message: Reflect.get(error, "message"),
    cause: Reflect.get(error, "cause"),
    error: Reflect.get(error, "error"),
    _tag: Reflect.get(error, "_tag"),
    stack: Reflect.get(error, "stack"),
  };
}

export const diagnosticLogger = {
  debug(message: string, ...values: readonly unknown[]): void {
    emitDiagnostic("debug", message, values);
  },
  info(message: string, ...values: readonly unknown[]): void {
    emitDiagnostic("info", message, values);
  },
  warn(message: string, ...values: readonly unknown[]): void {
    emitDiagnostic("warn", message, values);
  },
  error(message: string, ...values: readonly unknown[]): void {
    emitDiagnostic("error", message, values);
  },
};
